import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useParams,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { ShopProvider, useShopData } from './lib/shop-context.js';
import { initTelegram, startParam } from './lib/telegram.js';
import { cart } from './lib/cart.js';
import { EmptyState, Spinner } from './components/primitives.js';
import { CartPage } from './pages/cart.js';
import { CheckoutPage } from './pages/checkout.js';
import { OrderPage } from './pages/order.js';
import { ProductPage } from './pages/product.js';
import { ShopPage } from './pages/shop.js';

/**
 * Routing.
 *
 * The `/s/$slug` prefix is deliberate and permanent: §6.8 promises that a slug
 * pasted into a Facebook post keeps working forever, and §7 hangs the
 * OG-injecting Pages Function off `/s/{shop}/p/{product}`.
 */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <EmptyState
      title="TeleStore"
      hint="Open a shop link to start — for example /s/sakura-boutique"
    />
  ),
});

/**
 * Shop layout route. It loads the shop once and provides the resolved theme to
 * everything beneath, so switching pages never re-flashes an unstyled screen.
 */
const shopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$slug',
  component: ShopLayout,
});

function ShopLayout() {
  const { slug } = useParams({ from: '/s/$slug' });
  const state = useShopData(slug);

  useEffect(() => {
    cart.useShop(slug);
    // Persist the acquisition source on first landing so it survives the
    // customer browsing around before checking out (§6.3).
    const params = new URLSearchParams(window.location.search);
    const source = params.get('src');
    const ref = params.get('ref');
    if (source || ref) {
      try {
        sessionStorage.setItem(
          'telestore.attribution',
          JSON.stringify({ source, ref_code: ref }),
        );
      } catch {
        // non-fatal
      }
    }
    /**
     * §3 `promotion`: a shared link auto-applies its code. Captured here, on
     * the layout, so it works from a product link as well as the shop root —
     * a merchant pastes whichever link fits the Facebook post.
     */
    const promo = params.get('promo');
    if (promo) cart.addPromotionCode(promo);

    // The Mini App equivalent: `?startapp=p_SUMMER` from a bot deep link.
    // `p_` is the promo prefix used by `encodeStartPayload` (§6.3).
    const start = startParam();
    if (start?.startsWith('p_')) cart.addPromotionCode(start.slice(2));
  }, [slug]);

  useEffect(() => {
    if (state.status === 'ready') {
      initTelegram(state.data!.resolvedTheme.tokens['color-bg']);
    }
  }, [state.status, state.data]);

  if (state.status === 'loading') return <Spinner />;
  if (state.status === 'error' || !state.data) {
    return <EmptyState title="Shop not found" hint={state.error} />;
  }

  return (
    <ShopProvider data={state.data}>
      <Outlet />
    </ShopProvider>
  );
}

const shopIndexRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: '/',
  component: ShopPage,
});

const productRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: '/p/$productSlug',
  component: ProductPage,
});

const cartRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: '/cart',
  component: CartPage,
});

const checkoutRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: '/checkout',
  component: CheckoutPage,
});

const orderRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: '/order/$code',
  component: OrderPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  shopRoute.addChildren([shopIndexRoute, productRoute, cartRoute, checkoutRoute, orderRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Mid-range Android on Cambodian mobile data: keep the transition instant
  // and let the page fill in, rather than blocking on a pending loader.
  defaultPendingMs: 150,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
